import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput from '../components/DecimalInput';

function PasswordChangeCard() {
  const modal = useModal();
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return modal.error('La nueva contraseña debe tener al menos 8 caracteres');
    if (pw.next !== pw.confirm) return modal.error('La nueva contraseña y la repetición no coinciden');
    if (pw.current === pw.next) return modal.error('La nueva contraseña no puede ser igual a la actual');

    setSaving(true);
    try {
      await api.put('/auth/me/password', { currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      setShow({ current: false, next: false, confirm: false });
      modal.success('Contraseña actualizada');
    } catch (err) {
      modal.error(err.response?.data?.message || 'Error cambiando la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const PwField = ({ label, value, onChange, visible, toggle, autoFocus }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={visible ? 'text' : 'password'}
          className="form-input"
          style={{ paddingRight: 40 }}
          value={value}
          onChange={onChange}
          autoComplete={visible ? 'off' : 'new-password'}
          autoFocus={autoFocus}
          required
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={visible ? 'Ocultar' : 'Mostrar'}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#64748b', display: 'flex', alignItems: 'center', padding: 4,
          }}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit}>
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 className="card-title" style={{ marginBottom: 8 }}>Cambiar contraseña</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, marginBottom: 16 }}>
          Pide la contraseña actual por seguridad. Mínimo 8 caracteres.
        </p>
        <PwField
          label="Contraseña actual"
          value={pw.current}
          onChange={(e) => setPw({ ...pw, current: e.target.value })}
          visible={show.current}
          toggle={() => setShow((s) => ({ ...s, current: !s.current }))}
        />
        <PwField
          label="Nueva contraseña"
          value={pw.next}
          onChange={(e) => setPw({ ...pw, next: e.target.value })}
          visible={show.next}
          toggle={() => setShow((s) => ({ ...s, next: !s.next }))}
        />
        <PwField
          label="Repite la nueva contraseña"
          value={pw.confirm}
          onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
          visible={show.confirm}
          toggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </div>
    </form>
  );
}

const Settings = () => {
  const { user, activeTenant, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || ''
  });
  const [companyForm, setCompanyForm] = useState({
    name: activeTenant?.name || '',
    nif: activeTenant?.nif || '',
    address: activeTenant?.address || '',
    city: activeTenant?.city || '',
    postalCode: activeTenant?.postalCode || '',
    province: activeTenant?.province || '',
    phone: activeTenant?.phone || '',
    email: activeTenant?.email || ''
  });
  const [billingForm, setBillingForm] = useState({
    default_tax_rate: '21',
    default_irpf_rate: '15'
  });
  const [bootstrapSourceDir, setBootstrapSourceDir] = useState('/app/data/asesoria/salidas');
  const [reopenForm, setReopenForm] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), quarter: Math.floor(n.getMonth() / 3) + 1 };
  });
  const [reopening, setReopening] = useState(false);
  // Reabrir un trimestre cerrado: operación excepcional, por eso vive aquí
  // (acceso menos directo) y siempre pide confirmación con advertencia.
  const handleReopenQuarter = () => {
    const y = parseInt(reopenForm.year, 10);
    const q = parseInt(reopenForm.quarter, 10);
    if (!y || y < 2000 || y > 2100 || q < 1 || q > 4) return modal.error('Indica un año y un trimestre (1-4) válidos');
    modal.confirm(
      `ATENCIÓN: vas a REABRIR el trimestre T${q} de ${y}. Esto quita el cierre de TODAS las facturas y proformas de ese periodo y solo debería hacerse si lo cerraste por error. ¿Reabrir T${q} de ${y}?`,
      async () => {
        setReopening(true);
        try {
          const res = await api.post('/invoices/reopen-quarter', { year: y, quarter: q });
          modal.success(`Trimestre T${q} de ${y} reabierto: ${res.data.reopened} factura(s) y ${res.data.reopenedProformas} proforma(s).`);
        } catch (e) {
          modal.error(e.response?.data?.message || 'No se pudo reabrir el trimestre');
        } finally {
          setReopening(false);
        }
      }
    );
  };
  const modal = useModal();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/settings');
        const list = Array.isArray(res.data) ? res.data : [];
        const taxSetting = list.find(s => s.key === 'default_tax_rate');
        const irpfSetting = list.find(s => s.key === 'default_irpf_rate');
        setBillingForm({
          default_tax_rate: taxSetting ? taxSetting.value : '21',
          default_irpf_rate: irpfSetting ? irpfSetting.value : '15'
        });
      } catch (error) {
        modal.error('Error cargando configuración');
      }
    };
    loadSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put('/auth/me', { name: form.name });
      updateUser({ ...user, name: res.data.name });
      modal.success('Perfil actualizado');
    } catch (error) {
      modal.error('Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!activeTenant?.id) return modal.error('No hay empresa activa');
    setLoading(true);
    try {
      const res = await api.put(`/auth/me/tenant/${activeTenant.id}`, companyForm);
      updateUser({
        ...user,
        tenants: user.tenants.map(t =>
          t.id === activeTenant.id ? { ...t, ...res.data } : t
        )
      });
      modal.success('Datos de empresa actualizados');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/settings/bulk', {
        settings: [
          { key: 'default_tax_rate', value: billingForm.default_tax_rate, group: 'billing' },
          { key: 'default_irpf_rate', value: billingForm.default_irpf_rate, group: 'billing' }
        ]
      });
      modal.success('Configuración de facturación guardada');
    } catch (error) {
      modal.error('Error guardando configuración de facturación');
    } finally {
      setLoading(false);
    }
  };

  const handleBootstrap = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/automation/bootstrap-from-facturas', {
        sourceDir: bootstrapSourceDir
      });
      modal.success('Importación inicial completada');
      if (res.data.output) {
        console.log(res.data.output);
      }
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error en importación inicial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Configuración</h1>
          <p className="page-subtitle">Tu perfil, empresa activa y parámetros de facturación</p>
        </div>
      </div>

      {/* Datos personales */}
      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 20 }}>Datos personales</h2>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" value={user?.email || ''} disabled style={{ background: '#f1f5f9' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar perfil'}
            </button>
          </div>
        </div>
      </form>

      {/* Cambiar contraseña */}
      <PasswordChangeCard />

      {/* Empresa activa */}
      {activeTenant && (
        <form onSubmit={handleCompanySubmit}>
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 className="card-title" style={{ marginBottom: 20 }}>Mi empresa (emisor de facturas)</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nombre fiscal *</label>
                <input className="form-input" value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">NIF / CIF</label>
                <input className="form-input" value={companyForm.nif} onChange={e => setCompanyForm({ ...companyForm, nif: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-input" value={companyForm.address} onChange={e => setCompanyForm({ ...companyForm, address: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Ciudad</label>
                <input className="form-input" value={companyForm.city} onChange={e => setCompanyForm({ ...companyForm, city: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Código postal</label>
                <input className="form-input" value={companyForm.postalCode} onChange={e => setCompanyForm({ ...companyForm, postalCode: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Provincia</label>
              <input className="form-input" value={companyForm.province} onChange={e => setCompanyForm({ ...companyForm, province: e.target.value })} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={companyForm.phone} onChange={e => setCompanyForm({ ...companyForm, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email de contacto</label>
                <input type="email" className="form-input" value={companyForm.email} onChange={e => setCompanyForm({ ...companyForm, email: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar empresa'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Facturación */}
      <form onSubmit={handleBillingSubmit}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 8 }}>Facturación</h2>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 20 }}>
            Porcentajes por defecto al crear nuevas facturas. Se pueden modificar por línea en cada factura.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">IVA por defecto (%)</label>
              <DecimalInput
                min="0"
                max="100"
                step="0.1"
                value={billingForm.default_tax_rate}
                onValueChange={value => setBillingForm({ ...billingForm, default_tax_rate: value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Retención IRPF por defecto (%)</label>
              <DecimalInput
                min="0"
                max="100"
                step="0.1"
                value={billingForm.default_irpf_rate}
                onValueChange={value => setBillingForm({ ...billingForm, default_irpf_rate: value })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar configuración de facturación'}
            </button>
          </div>
        </div>
      </form>

      <div className="card" style={{ marginTop: 20 }}>
        <h2 className="card-title" style={{ marginBottom: 16 }}>Importación inicial (empresa + clientes + facturas)</h2>
        <p style={{ color: '#64748b', marginBottom: 14 }}>
          Ejecuta solo la primera vez. Lee PDFs de salidas, crea clientes, actualiza datos de tu empresa y reimporta facturas.
        </p>
        <form onSubmit={handleBootstrap}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Carpeta origen (salidas)</label>
              <input className="form-input" value={bootstrapSourceDir} onChange={e => setBootstrapSourceDir(e.target.value)} required />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Importando...' : 'Ejecutar importación inicial'}
            </button>
          </div>
        </form>
      </div>

      {/* Reabrir un trimestre cerrado — operación excepcional */}
      <div className="card" style={{ marginTop: 20 }}>
        <h2 className="card-title" style={{ marginBottom: 8 }}>Reabrir un trimestre cerrado</h2>
        <p style={{ color: '#64748b', marginBottom: 14 }}>
          Operación excepcional. Cerrar un trimestre se hace desde Facturas; reabrirlo solo
          debería hacerse si lo cerraste por error. Quita el cierre de todas las facturas y
          proformas del periodo indicado.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Año</label>
            <input
              className="form-input"
              type="number"
              value={reopenForm.year}
              onChange={e => setReopenForm({ ...reopenForm, year: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Trimestre</label>
            <select
              className="form-input"
              value={reopenForm.quarter}
              onChange={e => setReopenForm({ ...reopenForm, quarter: e.target.value })}
            >
              <option value={1}>T1 (ene-mar)</option>
              <option value={2}>T2 (abr-jun)</option>
              <option value={3}>T3 (jul-sep)</option>
              <option value={4}>T4 (oct-dic)</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-outline" disabled={reopening} onClick={handleReopenQuarter}>
            {reopening ? 'Reabriendo...' : 'Reabrir trimestre'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
